import {
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_SINGLE,
} from 'data/nodeTypes';
import { QUOTA_MATCHERS } from 'containers/QuestionnairePage/GenericQuestion/SingleMultipleQuestion/constants';
import { isNil, some, sumBy, map, omit, cloneDeep, isFinite } from 'lodash';
import Raven from 'raven-js';
import {
  generateQuotaRuleForAnswer,
  generateQuotaRuleForAnswerColumn,
} from 'containers/QuestionnaireLogic/utils';
import { remainingNeeded } from 'utils/minModeRemaining';

export const hasMissingQuota = (values = [], allowZeroQuota = false) =>
  some(
    values,
    ({ quotaCompletes }) =>
      isNil(quotaCompletes) || (quotaCompletes === 0 && !allowZeroQuota),
  );

export const hasQuotaLessThanCollected = (
  values = [],
  collectedQuotas = [],
) => {
  const flag = values.some((value) => {
    const { quota: remainingQuota, refId } = value;
    const collectedQuota = collectedQuotas.find((q) => q.answerRefId === refId);
    const collectedCount = collectedQuota ? collectedQuota.resultsCount : 0;

    return remainingQuota + collectedCount < collectedCount;
  });

  return flag;
};

export const hasWrongQuotaSum = (
  values = [],
  completes,
  matcher = QUOTA_MATCHERS.EQUAL,
  isUnderEdit = false,
  collectedQuotas = [],
) => {
  const remainingQuotaCompletes = sumBy(values, 'quotaCompletes');

  if (isUnderEdit) {
    const totalCollectedQuotas = sumBy(collectedQuotas, 'resultsCount');
    const quotaSum = remainingQuotaCompletes + totalCollectedQuotas;

    if (matcher === QUOTA_MATCHERS.GREATER_THAN_EQUAL) {
      return quotaSum < completes;
    }
    if (matcher === QUOTA_MATCHERS.EQUAL) {
      return quotaSum !== completes;
    }
  } else {
    if (matcher === QUOTA_MATCHERS.GREATER_THAN_EQUAL) {
      return remainingQuotaCompletes < completes;
    }
    if (matcher === QUOTA_MATCHERS.EQUAL) {
      return remainingQuotaCompletes !== completes;
    }
  }

  Raven.captureException('QUOTA-MATCH-Error', {
    extra: {
      values,
      completes,
      matcher,
      availableMatchers: QUOTA_MATCHERS,
      error: 'quotaMatcherMismatch',
    },
  });
  throw new Error('quotaMatcherMismatch');
};

export const updateQuota = (values = [], completes = 1, previousCompletes) =>
  map(values, (value) => {
    const previousQuota = (value.quotaCompletes * 100) / previousCompletes;
    const quotaCompletes = Math.round(completes * (previousQuota / 100));

    return {
      ...value,
      quotaCompletes,
    };
  });

export const fitQuota = (values, completes) => {
  const sum = sumBy(values, 'quotaCompletes');
  const diff = completes - sum;
  const absDiff = Math.abs(diff);
  let fixedValues = null;

  if (!diff) return values;

  if (diff < 0) {
    // sum exceeds the completes, need to reduce
    fixedValues = values
      .map(addIndexProp('customIndexForFittingQuota'))
      .sort(descending('quotaCompletes'))
      .map(
        quotaConditionalFixByIndex({
          step: -1,
          indexLimit: absDiff,
        }),
      )
      .sort(ascending('customIndexForFittingQuota'))
      .map(removeIndexProp('customIndexForFittingQuota'));
  } else {
    // missing some points to reach completes, need to increase
    fixedValues = values
      .map(addIndexProp('customIndexForFittingQuota'))
      .sort(ascending('quotaCompletes'))
      .map(
        quotaConditionalFixByIndex({
          step: +1,
          indexLimit: absDiff,
        }),
      )
      .sort(ascending('customIndexForFittingQuota'))
      .map(removeIndexProp('customIndexForFittingQuota'));
  }
  return fixedValues;
};

export const sumAudiencesCompletes = (audiences = []) =>
  sumBy(audiences, 'quotaCompletes');

export const adjustAudienceQuotaPercentages = (audiences = []) => {
  const totalCompletes = sumAudiencesCompletes(audiences);

  return audiences.map((audience) => ({
    ...audience,
    quota: (audience.quotaCompletes * 100) / totalCompletes,
  }));
};

// generates a function to be used in .map calls that will
// add @step while the index of the map is less thand @indexLimit
// and recalculate the quota property based on @completes
const quotaConditionalFixByIndex =
  ({ step, indexLimit }) =>
  (value, index) => {
    if (index < indexLimit) {
      const quotaCompletes = value.quotaCompletes + step;
      return {
        ...value,
        quotaCompletes,
      };
    }
    return value;
  };

const addIndexProp = (name) => (value, index) => ({ ...value, [name]: index });
const removeIndexProp = (name) => (value) => omit(value, [name]);

const ascending = (prop) => (a, b) => a[prop] - b[prop];
const descending = (prop) => (a, b) => b[prop] - a[prop];

export function addQuotaRulesForSpecificAnswersColumns({
  questionRefId,
  questionType,
  answers,
  scorepoints,
  columns,
  totalQuota,
  ql,
  isEligibleItem,
  isScreening = false,
}) {
  let newQl = cloneDeep(ql);
  switch (questionType) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE: {
      const quotaPerAnswerMap = spreadQuotaToItems({
        totalQuota,
        rows: answers,
        questionType,
        isEligibleItem,
      });
      newQl = generateQuotaRulesForSingleMultipleNode({
        questionRefId,
        quotaPerAnswerMap,
        ql: newQl,
        isScreening,
      });
      break;
    }
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE: {
      const quotaPerAnswerColumnMap = spreadQuotaToItems({
        totalQuota,
        rows: answers,
        columns: scorepoints,
        questionType,
        isEligibleItem,
      });
      newQl = generateQuotaRulesForMatrixNode({
        questionRefId,
        quotaPerAnswerColumnMap,
        ql: newQl,
      });
      break;
    }
    case QUESTION_MATRIX_BIPOLAR: {
      const quotaPerAnswerColumnMap = spreadQuotaToItems({
        totalQuota,
        rows: answers,
        columns,
        questionType,
        isEligibleItem,
      });
      newQl = generateQuotaRulesForMatrixNode({
        questionRefId,
        quotaPerAnswerColumnMap,
        ql: newQl,
      });
      break;
    }
    default:
      console.error(
        `[addQuotaRulesForSpecificAnswersColumns] - Question type ${questionType} not supported for quota rules generation`,
      );
      return ql;
  }
  return newQl;
}

function generateQuotaRulesForSingleMultipleNode({
  questionRefId,
  quotaPerAnswerMap,
  ql,
  isScreening,
}) {
  let newQl = cloneDeep(ql);
  Object.entries(quotaPerAnswerMap).forEach(([answerRefId, quotaValue]) => {
    newQl = generateQuotaRuleForAnswer({
      questionRefId,
      answerRefId,
      quotaValue,
      ql: newQl,
      isScreening,
    });
  });
  return newQl;
}

function generateQuotaRulesForMatrixNode({
  questionRefId,
  quotaPerAnswerColumnMap,
  ql,
}) {
  let newQl = cloneDeep(ql);
  Object.entries(quotaPerAnswerColumnMap).forEach(([key, quotaValue]) => {
    const [answerRefId, columnRefId] = key.split('::');
    newQl = generateQuotaRuleForAnswerColumn({
      questionRefId,
      answerRefId,
      columnRefId,
      quotaValue,
      ql: newQl,
    });
  });
  return newQl;
}

// Items `isEligibleItem` rejects get no quota; the total is spread over the
// eligible ones (per row on matrix), with the remainder on the last one.
function spreadQuotaToItems({
  totalQuota,
  rows,
  columns,
  questionType,
  isEligibleItem = () => true,
}) {
  // find valid answers based on business requirements
  const validRows = rows.filter(Boolean);

  switch (questionType) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE: {
      const eligibleRows = validRows.filter(({ refId }) =>
        isEligibleItem({ answerRefId: refId }),
      );
      return spreadOver(
        eligibleRows.map(({ refId }) => refId),
        totalQuota,
      );
    }
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE:
    case QUESTION_MATRIX_BIPOLAR: {
      const validColumns = columns.filter(Boolean);
      return validRows.reduce((acc, { refId: rowRefId }) => {
        const eligibleKeys = validColumns
          .filter(({ refId: columnRefId }) =>
            isEligibleItem({ answerRefId: rowRefId, columnRefId }),
          )
          .map(({ refId: columnRefId }) => `${rowRefId}::${columnRefId}`);
        return Object.assign(acc, spreadOver(eligibleKeys, totalQuota));
      }, {});
    }
    default:
      return {};
  }
}

function spreadOver(keys, totalQuota) {
  if (keys.length === 0) return {};

  const quotaPerItem = Math.floor(totalQuota / keys.length);
  const remainingQuotasToSpread = totalQuota % keys.length;
  const quotaPerKeyMap = keys.reduce((acc, key) => {
    acc[key] = quotaPerItem;
    return acc;
  }, {});
  // add remaining quota to the last item
  quotaPerKeyMap[keys[keys.length - 1]] += remainingQuotasToSpread;
  return quotaPerKeyMap;
}

/**
 * A 0 quota is valid, except a screening quota while the survey is a draft: it
 * would screen out everyone who picks that answer. See `Missing quota` in
 * CONTEXT.md.
 */
export const isZeroQuotaAllowed = ({ isDraft, isScreening }) =>
  !isDraft || !isScreening;

/**
 * Whether an item that should have a quota has no usable number: no quota, a
 * value that isn't a number, or 0 unless `allowZeroQuota`. A negative
 * remaining is not missing: in max mode it is a quota below collected, in min
 * mode it is overshoot. In min mode a 0 floor is valid, drafts included.
 */
export function hasMissingQuotaFromItems(
  itemKeys,
  nodeQuotaItems = {},
  { allowZeroQuota = false, isMinimumResponses = false } = {},
) {
  return itemKeys.some((itemKey) => {
    const remaining = isMinimumResponses
      ? remainingNeeded(nodeQuotaItems[itemKey]?.remaining)
      : nodeQuotaItems[itemKey]?.remaining;
    if (!isFinite(remaining)) return true;

    return !allowZeroQuota && !isMinimumResponses && remaining === 0;
  });
}

export function hasWrongQuotaSumFromItems(
  visibleItemKeys,
  nodeQuotaItems = [],
  remainingCompletes,
) {
  // assume it is always valid to exceed the completes
  const totalRemainingQuota = visibleItemKeys.reduce((sum, itemKey) => {
    const quotaInfo = nodeQuotaItems[itemKey] || {};
    return sum + (isFinite(quotaInfo.remaining) ? quotaInfo.remaining : 0);
  }, 0);

  return totalRemainingQuota < remainingCompletes;
}

export function hasWrongMinimumQuotaSumFromItems(
  visibleItemKeys,
  nodeQuotaItems = [],
  remainingCompletes,
) {
  const totalRemainingQuota = visibleItemKeys.reduce((sum, itemKey) => {
    const quotaInfo = nodeQuotaItems[itemKey] || {};
    const { remaining, revisionCount } = quotaInfo;
    const remainingQuota = isFinite(revisionCount)
      ? remaining - revisionCount
      : remaining;
    const needed = remainingNeeded(remainingQuota);

    return sum + (isFinite(needed) ? needed : 0);
  }, 0);

  return totalRemainingQuota > remainingCompletes;
}

export function hasWrongQuotaSumFromItemsMatrix({
  nodeQuotaItems = [],
  remainingCompletes,
  rows,
  columns,
}) {
  return rows.some((row) => {
    let totalQuota = 0;
    columns.forEach((column) => {
      const itemKey = `${row.refId}::${column.refId}`;
      const quotaInfo = nodeQuotaItems[itemKey] || {};
      const remainingQuota = isFinite(quotaInfo.remaining)
        ? quotaInfo.remaining
        : 0;

      totalQuota += remainingQuota;
    });

    if (totalQuota < remainingCompletes) {
      return true;
    }
    return false;
  });
}

export function hasWrongMinimumQuotaSumFromItemsMatrix({
  nodeQuotaItems = [],
  remainingCompletes,
  rows,
  columns,
}) {
  return rows.some((row) => {
    let totalQuota = 0;
    columns.forEach((column) => {
      const itemKey = `${row.refId}::${column.refId}`;
      const quotaInfo = nodeQuotaItems[itemKey] || {};
      const { remaining, revisionCount } = quotaInfo;
      const remainingQuota = isFinite(revisionCount)
        ? remaining - revisionCount
        : remaining;

      const needed = remainingNeeded(remainingQuota);

      totalQuota += isFinite(needed) ? needed : 0;
    });

    return totalQuota > remainingCompletes;
  });
}

export function hasQuotaLessThanCollectedFromItems(
  visibleItemKeys,
  nodeQuotaItems = [],
) {
  const someItemsHasLessThanCollectedQuota = visibleItemKeys.some((itemKey) => {
    const quotaInfo = nodeQuotaItems[itemKey] || {};
    const remainingQuota = isFinite(quotaInfo.remaining)
      ? quotaInfo.remaining
      : 0;

    return remainingQuota < 0;
  });

  return someItemsHasLessThanCollectedQuota;
}

export function hasQuotaFilledFromItemsMatrix(
  nodeQuotaItems = [],
  rows = [],
  columns = [],
) {
  return rows.some((row) => {
    let allExhausted = true;
    let hasCollected = false;
    columns.forEach((column) => {
      const itemKey = `${row.refId}::${column.refId}`;
      const quotaInfo = nodeQuotaItems[itemKey] || {};
      const { revisionCount = 0, collected = 0 } = quotaInfo;
      const remainingQuota = isFinite(quotaInfo.remaining)
        ? quotaInfo.remaining - revisionCount
        : 0;
      if (remainingQuota > 0) allExhausted = false;
      if (collected > 0) hasCollected = true;
    });
    return allExhausted && hasCollected;
  });
}

export function hasQuotaFilledFromItems(visibleItemKeys, nodeQuotaItems = []) {
  let hasCollected = false;
  const allExhausted = visibleItemKeys.every((itemKey) => {
    const quotaInfo = nodeQuotaItems[itemKey] || {};
    const { revisionCount = 0, collected = 0 } = quotaInfo;
    const remainingQuota = isFinite(quotaInfo.remaining)
      ? quotaInfo.remaining - revisionCount
      : 0;
    if (collected > 0) hasCollected = true;
    return remainingQuota <= 0;
  });
  return allExhausted && hasCollected;
}
